import React, { useEffect, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { getProjectInspectionScore } from '../../api/inspections';
import type { ProjectScoreSummary } from '../../api/inspections';

interface Props {
  projectId: string;
}

function scoreStyles(score: number | null): {
  backgroundColor: string;
  border: string;
  color: string;
} {
  if (score === null)
    return {
      backgroundColor: '#F8FAFC',
      border: '1px solid #CBD5E1',
      color: '#94A3B8'
    };
  if (score >= 4)
    return {
      backgroundColor: '#E1F5EE',
      border: '1px solid #34C98033',
      color: '#0F6E56'
    };
  if (score >= 2.5)
    return {
      backgroundColor: '#FAEEDA',
      border: '1px solid #F59E0B33',
      color: '#BA7517'
    };
  return {
    backgroundColor: '#FCEBEB',
    border: '1px solid #E24B4A33',
    color: '#A32D2D'
  };
}

function ScorePill({
  label,
  score,
  tooltip
}: {
  label: string;
  score: number;
  tooltip: string;
}) {
  const styles = scoreStyles(score);
  return (
    <Tooltip title={tooltip}>
      <Box
        sx={{
          ...styles,
          alignItems: 'center',
          borderRadius: '24px',
          display: 'inline-flex',
          gap: '3px',
          px: '8px',
          py: '2px'
        }}
      >
        <Typography
          sx={{
            color: styles.color,
            fontSize: '11px',
            fontWeight: 600,
            lineHeight: 1.4
          }}
        >
          {score.toFixed(1)}
        </Typography>
        <Typography
          sx={{
            color: styles.color,
            fontSize: '10px',
            fontWeight: 400,
            lineHeight: 1.4,
            opacity: 0.75
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
}

const ProjectInspectionBadge: React.FC<Props> =
  function ProjectInspectionBadge({ projectId }) {
    const [summary, setSummary] = useState<ProjectScoreSummary | null>(null);

    useEffect(() => {
      getProjectInspectionScore(projectId)
        .then(setSummary)
        .catch(() => {});
    }, [projectId]);

    if (!summary || (summary.lastScore === null && summary.avgScore === null))
      return null;

    const last = summary.lastScore !== null ? Number(summary.lastScore) : null;
    const avg = summary.avgScore !== null ? Number(summary.avgScore) : null;

    return (
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
        {last !== null && (
          <ScorePill
            label="last"
            score={last}
            tooltip="Last inspection score"
          />
        )}
        {avg !== null && (
          <ScorePill
            label="avg"
            score={avg}
            tooltip="Average inspection score"
          />
        )}
      </Box>
    );
  };

export default ProjectInspectionBadge;
